import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import LockIcon from '@mui/icons-material/Lock';
import StorageIcon from '@mui/icons-material/Storage';
import ShareIcon from '@mui/icons-material/Share';
import CookieIcon from '@mui/icons-material/Cookie';
import UpdateIcon from '@mui/icons-material/Update';
import ContactMailIcon from '@mui/icons-material/ContactMail';

const sections = [
  {
    icon: DataUsageIcon,
    title: 'Information We Collect',
    content: `We collect information that you provide directly to us, including:
    • Personal information (name, email, phone number)
    • Device information for repair services
    • Payment information
    • Communication records`,
  },
  {
    icon: StorageIcon,
    title: 'How We Use Your Information',
    content: `Your information helps us:
    • Process your repair requests
    • Communicate about your device status
    • Improve our services
    • Send relevant updates and offers
    • Maintain service quality`,
  },
  {
    icon: ShareIcon,
    title: 'Information Sharing',
    content: `We do not sell your personal information. We may share your information with:
    • Service providers and repair partners
    • Payment processors
    • Legal authorities when required
    All sharing is conducted with appropriate security measures.`,
  },
  {
    icon: LockIcon,
    title: 'Data Security',
    content: `We implement robust security measures:
    • Encryption of sensitive data
    • Secure data storage systems
    • Regular security audits
    • Staff security training
    • Access controls`,
  },
  {
    icon: CookieIcon,
    title: 'Cookies and Tracking',
    content: `We use cookies and similar technologies to:
    • Improve user experience
    • Analyze site usage
    • Remember your preferences
    • Provide secure authentication
    You can control cookie settings in your browser.`,
  },
  {
    icon: UpdateIcon,
    title: 'Policy Updates',
    content: `We may update this policy periodically. We will notify you of any material changes via:
    • Email notification
    • Website announcement
    • App notification`,
  },
  {
    icon: ContactMailIcon,
    title: 'Contact Us',
    content: `For privacy-related inquiries:
    • Email: privacy@akdmeshop.com
    • Phone: 1-800-AKDME
    • Address: [Your Business Address]
    We aim to respond within 48 hours.`,
  },
];

function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}>
      <Container>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{
              mb: 3,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Your privacy is important to us. This policy outlines how we collect,
            use, and protect your personal information.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 4 }}>
            <List>
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <Divider
                        sx={{
                          my: 4,
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                      />
                    )}
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        p: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          width: '100%',
                        }}
                      >
                        <ListItemIcon>
                          <Icon
                            sx={{
                              color: '#FF6B6B',
                              fontSize: 32,
                            }}
                          />
                        </ListItemIcon>
                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{ fontWeight: 600 }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'pre-line',
                              pl: 6,
                            }}
                          >
                            {section.content}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <SecurityIcon
            sx={{
              fontSize: 48,
              color: '#FF6B6B',
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Last updated: March 2025
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
